const parseHtml = (htmlContent: string): HTMLElement => {
  const parser = new DOMParser()
  return parser.parseFromString(htmlContent, 'text/html').body
}

export const getHtmlTextLength = (htmlContent: string): number => {
  if (!htmlContent) return 0
  return parseHtml(htmlContent).textContent?.length ?? 0
}

export const stripHtmlTags = (htmlContent: string): string => {
  if (!htmlContent) return ''
  return parseHtml(htmlContent).textContent ?? ''
}

export const sanitizeHtmlContent = (htmlContent: string): string => {
  if (!htmlContent) return ''
  const body = parseHtml(htmlContent)
  const dangerousElements = body.querySelectorAll('script, style, object, embed, iframe')
  for (const element of dangerousElements) {
    element.remove()
  }
  return body.textContent ?? ''
}

const DANGEROUS_URL_PROTOCOLS = /^(javascript|data|vbscript):/i
const DANGEROUS_URL_ATTRIBUTES = new Set(['href', 'src', 'action', 'formaction', 'xlink:href'])

const normalizeUrlValue = (value: string): string => value.replaceAll(/\s+/g, '').toLowerCase()

export const sanitizeHtmlToSafeHtml = (htmlContent: string): string => {
  if (!htmlContent) return ''
  const body = parseHtml(htmlContent)
  body.querySelectorAll('script, style, object, embed, iframe, link, meta, base').forEach((el) => el.remove())
  body.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const isDangerousEvent = attr.name.startsWith('on')
      const isDangerousUrl =
        DANGEROUS_URL_ATTRIBUTES.has(attr.name) && DANGEROUS_URL_PROTOCOLS.test(normalizeUrlValue(attr.value))
      if (isDangerousEvent || isDangerousUrl) {
        el.removeAttribute(attr.name)
      }
    }
  })
  return body.innerHTML
}

/**
 * Replaces non-breaking spaces (&nbsp; → U+00A0) and other non-standard
 * Unicode whitespace characters with a regular ASCII space so the mobile app
 * never receives named character references in the output.
 * @param text - The text to normalize.
 * @returns The normalized text.
 */
const normalizeTextContent = (text: string): string =>
  text.replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000\uFEFF\u00AD]/g, ' ')

/**
 * Converts rich-text HTML to the limited subset supported by the mobile app:
 * only <b> and <br> tags are kept.
 *
 * - <strong> / <b>  → <b>…</b>
 * - <div> / <p>     → <br> line-break before their content (block behaviour)
 * - <br>            → <br>
 * - Everything else → stripped, children kept as plain text
 * - Named character references (&nbsp; etc.) → plain ASCII space
 * @param nodes - The nodes to convert to mobile HTML.
 * @returns The converted HTML string.
 */
const nodesToMobileHtml = (nodes: NodeList | Node[]): string => {
  let result = ''

  for (const node of Array.from(nodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      result += normalizeTextContent(node.textContent ?? '')
      continue
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue

    const el = node as Element
    const tag = el.tagName.toLowerCase()
    const children = nodesToMobileHtml(el.childNodes)

    if (tag === 'b' || tag === 'strong') {
      result += `<b>${children}</b>`
    } else if (tag === 'br') {
      result += '<br>'
    } else if (tag === 'div' || tag === 'p') {
      if (result !== '') result += '<br>'
      result += children
    } else {
      result += children
    }
  }

  return result
}

/**
 * Sanitizes HTML content for the mobile app.
 * @param htmlContent - The HTML content to sanitize.
 * @returns The sanitized HTML content.
 */
export const sanitizeHtmlForMobileApp = (htmlContent: string): string => {
  if (!htmlContent) return ''
  const body = parseHtml(htmlContent)
  return nodesToMobileHtml(body.childNodes)
}
